import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, DataList, DataRow, Modal, ModalActions } from '@evogard/ui';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { BENEFICIARIES_KEY, BeneficiaryFormModal } from './BeneficiaryFormModal.js';
import { ACCOUNT_TYPE_LABELS, bankLabel, ownerKindLabel } from './labels.js';

/**
 * Um beneficiário com agência e conta SEM máscara — o que a lista não mostra.
 *
 * A leitura é `GET /platform/beneficiaries/:id`, que exige
 * `platform.beneficiaries:ver_dados_bancarios` e deixa cada abertura na trilha
 * de auditoria. Por isso ela acontece aqui, quando o modal abre, e não na
 * lista: abrir é o ato que a trilha registra.
 *
 * Editar também sai daqui, e pelo mesmo motivo: o formulário precisa dos
 * números inteiros — com os mascarados, salvar gravaria `•••89-0` por cima da
 * conta.
 */
export function BeneficiaryDetailModal({
  beneficiaryId,
  onClose,
  startEditing = false,
}: {
  /** `null` fecha o modal. */
  beneficiaryId: number | null;
  onClose: () => void;
  /** Abre direto no formulário de edição — a ação "Editar" da lista. */
  startEditing?: boolean;
}) {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'platform.beneficiaries', 'editar');
  const [editing, setEditing] = useState(false);
  const open = beneficiaryId !== null;

  const query = useQuery({
    queryKey: [...BENEFICIARIES_KEY, 'detalhe', beneficiaryId],
    enabled: open,
    /*
     * Sem `staleTime`: o padrão (zero) refaz a consulta a cada abertura, e cada
     * abertura é um registro novo na auditoria — que é o que ela deve ser.
     */
    queryFn: async () => {
      const r = await api.beneficiaries.getBeneficiary({
        params: { id: beneficiaryId! },
      });
      return ok(r, 200, 'Não foi possível abrir os dados bancários.');
    },
  });

  const b = query.data;
  const close = () => {
    setEditing(false);
    onClose();
  };

  if (b && (editing || startEditing)) {
    return (
      <BeneficiaryFormModal
        open={open}
        onOpenChange={(next) => !next && close()}
        beneficiary={b}
      />
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && close()}
      title={b?.owner.name ?? 'Beneficiário'}
      descricao={b ? ownerKindLabel(b) : undefined}
      size="md"
    >
      <div className="flex flex-col gap-4">
        {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
        {query.isLoading && <p className="text-sm text-texto-suave">Carregando…</p>}

        {b && (
          <>
            <DataList>
              <DataRow label="CPF/CNPJ de quem recebe">{b.document ?? '—'}</DataRow>
              {b.accountHolderName && (
                <DataRow label="Titular da conta">{b.accountHolderName}</DataRow>
              )}
              <DataRow label="Banco">
                {bankLabel(b.bankCode, b.bankName) ?? 'Sem conta'}
              </DataRow>
              <DataRow label="Agência">{b.agency ?? '—'}</DataRow>
              <DataRow label="Conta">{b.accountNumber ?? '—'}</DataRow>
              <DataRow label="Tipo de conta">
                {b.accountType ? ACCOUNT_TYPE_LABELS[b.accountType] : '—'}
              </DataRow>
              <DataRow label="Chave PIX">
                <span className="break-all">{b.pixKey ?? 'Sem chave'}</span>
              </DataRow>
            </DataList>
            <p className="text-xs text-texto-suave">
              Esta consulta fica registrada na trilha de auditoria.
            </p>
          </>
        )}

        <ModalActions className="border-t border-borda-suave pt-4">
          <Button variant="discreto" onClick={close}>
            Fechar
          </Button>
          {b && canEdit && !b.archivedAt && (
            <Button icon="etiqueta" onClick={() => setEditing(true)}>
              Editar dados bancários
            </Button>
          )}
        </ModalActions>
      </div>
    </Modal>
  );
}
